//import _ from "lodash";
import React, { useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import withStyles from "@material-ui/core/styles/withStyles";
import loadForm from "../../core-module/hoc/loadForm";
import config from "../../config";
import Grid from "@material-ui/core/Grid";
import AddIcon from "@material-ui/icons/Add";
import { trackPage } from "../../analytics";
import {
  clearMetaEventId,
  postSignupFormWithMetaConversion,
  trackMetaSignupFormSubmission
} from "../../metaConversions";

import * as api from "../../core-module/services/api";

import EasySelect from "../../common/components/EasyForm/EasySelect";
import EasyAutocomplete from "../../common/components/EasyForm/EasyAutocomplete";
// import EasyAutoSelect from "common/components/EasyForm/EasyAutoSelect";
import EasyRadio from "../../common/components/EasyForm/EasyRadio";
import EasyOpenInput from "../../common/components/EasyForm/EasyOpenInput";
import MultilineOpenInput from "../../common/components/EasyForm/MultilineOpenInput";
import EasyCanadaRegionDropdown from "../../common/components/EasyForm/EasyCanadaRegionDropdown";
import EasyCanadaPostalCode from "../../common/components/EasyForm/EasyCanadaPostalCode";
import EasyPhoneNumber from "../../common/components/EasyForm/EasyPhoneNumber";
import EasyErrorMessage from "../../common/components/EasyForm/EasyErrorMessage";
import SignupRecaptcha from "../../common/components/SignupRecaptcha/SignupRecaptcha";
import { SignupCard, CtaButton, StepHeader } from "../../signupComponents";
import Typography from "@material-ui/core/Typography";
import { persistor } from "../../store";

// import ContactInfo from './contactInfo';
import styles from "./styles";

const withSignupEventCode = (path, signupEventCode) =>
  signupEventCode
    ? `${path}?signupEventCode=${encodeURIComponent(signupEventCode)}`
    : path;

const longFormCommonRequiredFields = [
  { propName: "mailingAddress", label: "Mailing address" },
  { propName: "town", label: "Town" },
  { propName: "regionid", label: "Province" },
  { propName: "postalcode", label: "Postal code" },
  { propName: "emailAddress", label: "Email address" },
  { propName: "phoneNumber1", label: "Phone number" },
  { propName: "phoneType1", label: "Phone type" },
  {
    propName: "phoneTextme1",
    label: "SMS communication preference",
    message: "Please choose whether we can use SMS for communication."
  },
  {
    propName: "advSrc",
    label: "Where did you hear about MCSnet",
    message: "Please tell us where you heard about MCSnet."
  }
];

const longFormPersonalRequiredFields = [
  { propName: "firstName1", label: "First name" },
  { propName: "lastName1", label: "Last name" }
];

const longFormBusinessRequiredFields = [
  { propName: "firstName1", label: "Company name" },
  { propName: "firstName2", label: "Contact first name" },
  { propName: "lastName2", label: "Contact last name" }
];

const longFormInsideLocationRequiredFields = [
  { propName: "areaid", label: "Service area" },
  { propName: "serviceAddress", label: "Service address" }
];

const longFormLegalLandFields = [
  { propName: "ats_quarter", label: "Quarter" },
  { propName: "ats_section", label: "Section" },
  { propName: "ats_township", label: "Township" },
  { propName: "ats_range", label: "Range" },
  { propName: "ats_meridian", label: "Meridian" }
];

const isLongFormRequiredFieldEmpty = (propName, value) =>
  value == null ||
  String(value).trim() === "" ||
  (propName === "regionid" && String(value) === "0");

const getLongFormRequiredFields = (connectForm, overrideValues = {}) => {
  const getValue = propName =>
    Object.prototype.hasOwnProperty.call(overrideValues, propName)
      ? overrideValues[propName]
      : connectForm.getValue(propName);
  const requiredFields = [];

  if (getValue("accountType") === "c") {
    requiredFields.push(...longFormBusinessRequiredFields);
  } else {
    requiredFields.push(...longFormPersonalRequiredFields);
  }

  requiredFields.push(...longFormCommonRequiredFields);

  if (getValue("signupclass") === "inside") {
    requiredFields.push(...longFormInsideLocationRequiredFields);
  }

  return requiredFields;
};

const getLongFormValidationErrors = (
  connectForm,
  overrideValues = {},
  options = {}
) => {
  const validationErrors = {};
  const getValue = propName =>
    Object.prototype.hasOwnProperty.call(overrideValues, propName)
      ? overrideValues[propName]
      : connectForm.getValue(propName);

  getLongFormRequiredFields(connectForm, overrideValues).forEach(field => {
    if (
      isLongFormRequiredFieldEmpty(field.propName, getValue(field.propName))
    ) {
      validationErrors[field.propName] = {
        ...field,
        message: field.message || `${field.label} is required.`
      };
    }
  });

  if (getValue("signupclass") === "outside") {
    const hasMunicipalAddress = !isLongFormRequiredFieldEmpty(
      "serviceAddress",
      getValue("serviceAddress")
    );
    const legalLandValues = longFormLegalLandFields.map(field => ({
      ...field,
      value: getValue(field.propName)
    }));
    const hasAnyLegalLandValue = legalLandValues.some(
      field => !isLongFormRequiredFieldEmpty(field.propName, field.value)
    );
    const hasCompleteLegalLandDescription = legalLandValues.every(
      field => !isLongFormRequiredFieldEmpty(field.propName, field.value)
    );

    if (!hasMunicipalAddress && !hasCompleteLegalLandDescription) {
      if (hasAnyLegalLandValue) {
        legalLandValues.forEach(field => {
          if (isLongFormRequiredFieldEmpty(field.propName, field.value)) {
            validationErrors[field.propName] = {
              propName: field.propName,
              label: field.label,
              message:
                field.label + " is required for a legal land description."
            };
          }
        });
      } else {
        validationErrors.serviceAddress = {
          propName: "serviceAddress",
          label: "Municipal address or legal land description",
          message:
            "Enter a municipal address or complete all legal land description fields."
        };
      }
    }
  }

  if (
    options.needCaptcha &&
    isLongFormRequiredFieldEmpty("recaptcha", getValue("recaptcha"))
  ) {
    validationErrors.recaptcha = {
      propName: "recaptcha",
      label: "reCAPTCHA",
      message: "Please complete the reCAPTCHA check before submitting."
    };
  }

  return validationErrors;
};

const Form = function(props) {
  const [searchq, setSearchq] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const [requiredFieldErrors, setRequiredFieldErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const submissionInFlightRef = useRef(false);

  useEffect(() => {
    trackPage("form");
  }, []);

  useEffect(() => {
    if (props.connectForm.getValue("accountType") === "") {
      props.connectForm.change("p", "accountType");
    }
  }, [props.connectForm]);

  useEffect(() => {
    if (
      props.connectForm.getValue("signupclass") === "" ||
      props.connectForm.getValue("signupclass") == null ||
      (props.connectForm.getValue("signupclass") === "inside" &&
        (props.connectForm.getValue("areaid") === "" ||
          props.connectForm.getValue("areaid") == null))
    ) {
      props.startOver();
    }
  }, []);

  const stepNum =
    props.connectForm.getValue("signupclass") === "inside" ? 3 : 2;

  const onBackFromStepHeader = () => {
    // same behavior as CommonCardContent.goBack
    submissionInFlightRef.current = false;
    persistor.pause();
    persistor.flush().then(() =>
      persistor.purge().then(() => {
        sessionStorage.removeItem("signupformPersistentState");
        clearMetaEventId();
        if (
          props.location.pathname === "/inside" ||
          (props.location.pathname === "/form" && stepNum === 2) // STEP 02
        ) {
          props.history.push(
            withSignupEventCode(
              config.SIGNUP.APP_PATH + "/",
              props.signupEventCode
            )
          );
        } else if (props.location.pathname === "/form" && stepNum === 3) {
          props.history.push(
            withSignupEventCode(
              config.SIGNUP.APP_PATH + "/inside",
              props.signupEventCode
            )
          );
        } else {
          window.location.reload();
        }
      })
    );
  };

  useEffect(() => {}, [options]);

  async function updateOptionsState() {
    let res = null;
    if (props.connectForm.getValue("signupclass") === "inside")
      res = await api.get(
        `guest.1/serviceaddresssearch&searchq=${searchq}&areaid=${props.areaId}`
      );
    if (props.connectForm.getValue("signupclass") === "outside")
      res = await api.get(`guest.1/ruraladdresssearch&searchq=${searchq}`);
    if (res === null || !res.list) return;
    const newOptions = res.list.map(item => {
      if (item.id == null) {
        return { value: "_NULL", label: item.address };
      }
      return { value: item.id, label: item.address };
    });
    setOptions(newOptions);
  }

  useEffect(() => {
    if (searchq === null || (searchq.length || 0) < 2) {
      setOptions([]);
      return;
    }
    updateOptionsState();
  }, [searchq]);

  const { classes } = props;
  const additionalPhones = [];

  // const interacted = [];

  const clearForm = () => {
    submissionInFlightRef.current = false;
    persistor.pause();
    persistor.flush().then(() =>
      persistor.purge().then(() => {
        sessionStorage.removeItem("signupformPersistentState");
        clearMetaEventId();
        window.location.reload();
      })
    );
  };

  for (let i = 0; i < props.connectForm.getValue("additionalPhones"); i++) {
    additionalPhones.push(i + 2);
  }

  const handleAutoCompleteInputChange = (e, value) => {
    setSearchq(value);
  };

  const accountType = props.connectForm.getValue("accountType");
  const gigair = props.connectForm.getValue("gigair");
  const matchingFirstName = props.connectForm.getValue(
    accountType === "c" ? "firstName2" : "firstName1"
  );
  const matchingLastName = props.connectForm.getValue(
    accountType === "c" ? "lastName2" : "lastName1"
  );
  const matchingEmail = props.connectForm.getValue("emailAddress");
  const matchingPhone = props.connectForm.getValue("phoneNumber1");
  const matchingPostalCode = props.connectForm.getValue("postalcode");

  const longFormRequiredFields = getLongFormRequiredFields(props.connectForm);
  const missingRequiredFields = Object.values(requiredFieldErrors);
  const oldFormConnectForm = {
    ...props.connectForm,
    change: (value, propName) => {
      if (Object.keys(requiredFieldErrors).length > 0) {
        setRequiredFieldErrors(
          getLongFormValidationErrors(
            props.connectForm,
            {
              [propName]: value
            },
            {
              needCaptcha: props.needCaptcha
            }
          )
        );
      }
      return props.connectForm.change(value, propName);
    },
    getState: propName => {
      if (requiredFieldErrors[propName]) {
        return "Error";
      }
      return props.connectForm.getState(propName);
    },
    getErrorMessage: propName => {
      if (requiredFieldErrors[propName]) {
        return requiredFieldErrors[propName].message;
      }
      return props.connectForm.getErrorMessage(propName);
    },
    isRequired: propName =>
      Boolean(requiredFieldErrors[propName]) ||
      longFormRequiredFields.some(field => field.propName === propName) ||
      props.connectForm.isRequired(propName)
  };

  const focusFormField = propName => {
    const field = document.getElementById(propName);
    if (field && field.scrollIntoView) {
      field.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (field && field.focus) {
      field.focus({ preventScroll: true });
    }
  };

  const focusFirstFormError = () => {
    if (typeof props.connectForm.getAllState !== "function") {
      window.scrollTo(0, 0);
      return;
    }

    const allState = props.connectForm.getAllState();
    const firstErrorStateKey = Object.keys(allState).find(
      key => key.endsWith("State") && allState[key] === "Error"
    );

    if (firstErrorStateKey) {
      focusFormField(firstErrorStateKey.replace(/State$/, ""));
      return;
    }

    window.scrollTo(0, 0);
  };

  const focusRequiredField = field => {
    focusFormField(field.focusPropName || field.propName);
  };

  const setRecaptchaValidationMessage = message => {
    setRequiredFieldErrors(currentErrors => {
      const nextErrors = { ...currentErrors };
      if (message) {
        nextErrors.recaptcha = {
          propName: "recaptcha",
          label: "reCAPTCHA",
          message
        };
      } else {
        delete nextErrors.recaptcha;
      }
      return nextErrors;
    });
  };

  const submitForm = () => {
    if (submissionInFlightRef.current) return;

    const nextRequiredFieldErrors = getLongFormValidationErrors(
      props.connectForm,
      {},
      { needCaptcha: props.needCaptcha }
    );

    if (Object.keys(nextRequiredFieldErrors).length > 0) {
      setRequiredFieldErrors(nextRequiredFieldErrors);
      const firstMissingField = Object.values(nextRequiredFieldErrors)[0];
      if (firstMissingField) {
        setTimeout(() => focusRequiredField(firstMissingField), 0);
      } else {
        window.scrollTo(0, 0);
      }
      return;
    }

    setRequiredFieldErrors({});
    window.scrollTo(0, 0);
    submissionInFlightRef.current = true;
    setIsSubmitting(true);

    postSignupFormWithMetaConversion(props.connectForm.post, payload => {
      const nextPayload = { ...payload };

      if (String(nextPayload.promoCode || "").trim() === "") {
        delete nextPayload.promoCode;
      }

      if (props.signupEventCode) {
        nextPayload.signupEventCode = props.signupEventCode;
      }

      return nextPayload;
    })
      .then(({ response: res, submittedMetaEventId }) => {
        if (!submissionInFlightRef.current) return;

        if (res === false) {
          submissionInFlightRef.current = false;
          setIsSubmitting(false);
          setTimeout(focusFirstFormError, 0);
          return;
        }
        if (res != null && typeof res === "object") {
          if ("contactid" in res) {
            props.setContactid(res.contactid);
          }
          if ("regularpackages" in res && Array.isArray(res.regularpackages)) {
            props.setRegularPackages(res.regularpackages);
          } else {
            props.setRegularPackages([]);
          }

          trackMetaSignupFormSubmission(submittedMetaEventId, {
            em: matchingEmail,
            ph: matchingPhone,
            fn: matchingFirstName,
            ln: matchingLastName,
            zp: matchingPostalCode
          });
          clearMetaEventId();

          if (gigair) {
            props.history.push(
              withSignupEventCode(
                config.SIGNUP.APP_PATH + "/fin-gigair",
                props.signupEventCode
              )
            );
          } else if (accountType === "p") {
            props.history.push(
              withSignupEventCode(
                config.SIGNUP.APP_PATH + "/fin-residential",
                props.signupEventCode
              )
            );
          } else {
            props.history.push(
              withSignupEventCode(
                config.SIGNUP.APP_PATH + "/fin-business",
                props.signupEventCode
              )
            );
          }
          return;
        }

        submissionInFlightRef.current = false;
        setIsSubmitting(false);
      })
      .catch(error => {
        submissionInFlightRef.current = false;
        setIsSubmitting(false);
        console.error("Signup submit failed:", error);
      });
  };

  const fillMockData = () => {
    const mockEntries = [
      ["p", "accountType"],
      ["Siri Annthony", "firstName1"],
      ["Test", "lastName1"],
      ["4810 50th Ave", "mailingAddress"],
      ["St Paul", "town"],
      ["1", "regionid"],
      ["T0A 3A0", "postalcode"],
      ["test@example.com", "emailAddress"],
      ["(780) 645-4417", "phoneNumber1"],
      ["wo", "phoneType1"],
      ["no", "phoneTextme1"],
      ["60012 SEC HWY 881", "serviceAddress"],
      ["Online Advertisement", "advSrc"]
    ];

    const applyNext = index => {
      if (index >= mockEntries.length) {
        setTimeout(() => {
          const submitButton = document.getElementById("submitResidental");
          if (submitButton && submitButton.scrollIntoView) {
            submitButton.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          } else {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: "smooth"
            });
          }
        }, 0);
        return;
      }
      const [value, propName] = mockEntries[index];
      props.connectForm.change(value, propName);
      setTimeout(() => applyNext(index + 1), 0);
    };

    applyNext(0);
  };

  return (
    <Grid container justify="center">
      <Grid item xs={12}>
        {/* hard cap + centering wrapper */}
        <div style={{ width: "100%", maxWidth: 860, margin: "0 auto" }}>
          <SignupCard size="xl" maxw={860}>
            {config.IS_LOCAL_ENV ? (
              <Grid item xs={12} className={classes.title}>
                <Grid container justify="flex-end">
                  <CtaButton
                    onClick={fillMockData}
                    color="primary"
                    style={{ textTransform: "none" }}
                  >
                    Fill Mock Data
                  </CtaButton>
                </Grid>
              </Grid>
            ) : null}
            <StepHeader
              step={stepNum}
              title={
                "Tell us a little about yourself - we'll show your pricing next."
              }
              onBack={onBackFromStepHeader}
            />

            <Grid item xs={12} className={classes.title}>
              <br />
              {missingRequiredFields.length > 0 ? (
                <div className={classes.validationSummary} role="alert">
                  <Typography className={classes.validationSummaryTitle}>
                    Please complete the highlighted required fields.
                  </Typography>
                  <ul className={classes.validationSummaryList}>
                    {missingRequiredFields.map(field => (
                      <li key={field.propName}>
                        <button
                          className={classes.validationSummaryLink}
                          type="button"
                          onClick={() => focusRequiredField(field)}
                        >
                          {field.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <EasyErrorMessage connectForm={props.connectForm} />
            </Grid>

            <div className={classes.mobilePadding}>
              <Typography variant="h5" style={styles.titleStyles}>
                {" "}
                Contact Info{" "}
              </Typography>
              <Grid item xs={12} className={classes.title}>
                <Typography style={styles.typography}>
                  I want Internet for *
                </Typography>
                <EasyRadio
                  propName="accountType"
                  connectForm={oldFormConnectForm}
                />
              </Grid>

              <Grid item xs={12} className={classes.easyButtonStl} />

              <Grid
                item
                xs={12}
                md={props.connectForm.getValue("accountType") === "p" ? 6 : 12}
                className={classes.title}
              >
                <EasyOpenInput
                  propName="firstName1"
                  connectForm={oldFormConnectForm}
                />
              </Grid>

              {props.connectForm.getValue("accountType") === "p" ? (
                <Grid
                  key={"personallastname"}
                  item
                  xs={12}
                  md={6}
                  className={classes.title}
                >
                  <EasyOpenInput
                    connectForm={oldFormConnectForm}
                    propName="lastName1"
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} md={6} className={classes.title}>
                <EasyOpenInput
                  connectForm={oldFormConnectForm}
                  propName="firstName2"
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.title}>
                <EasyOpenInput
                  connectForm={oldFormConnectForm}
                  propName="lastName2"
                />
              </Grid>
              <Grid item xs={12} className={classes.title}>
                <EasyOpenInput
                  connectForm={oldFormConnectForm}
                  propName="mailingAddress"
                />
              </Grid>
              <Grid item xs={12} className={classes.title}>
                <EasyOpenInput
                  connectForm={props.connectForm}
                  propName="mailingAddress2"
                />
              </Grid>
              <Grid item xs={12} className={classes.title}>
                <EasyOpenInput
                  connectForm={oldFormConnectForm}
                  propName="town"
                />
              </Grid>
              <Grid item xs={6} className={classes.title}>
                <Typography>Province *</Typography>

                <EasyCanadaRegionDropdown
                  connectForm={oldFormConnectForm}
                  propName="regionid"
                />
              </Grid>
              <Grid item xs={6} className={classes.title}>
                <EasyCanadaPostalCode
                  connectForm={oldFormConnectForm}
                  propName="postalcode"
                />
              </Grid>
              <Grid item xs={12} className={classes.title}>
                <EasyOpenInput
                  connectForm={oldFormConnectForm}
                  propName="emailAddress"
                  inputProps={{ type: "email" }}
                />
              </Grid>
              <Grid item xs={12} className={classes.title}>
                <Grid container justify="center" align="flex-end">
                  <Grid item xs={8} className={classes.title}>
                    <EasyPhoneNumber
                      connectForm={oldFormConnectForm}
                      propName="phoneNumber1"
                    />
                  </Grid>
                  <Grid item xs={4} className={classes.title}>
                    <EasyOpenInput
                      connectForm={props.connectForm}
                      propName="phoneExt1"
                      inputProps={{ type: "number" }}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} className={classes.title}>
                  <Typography> PHONE TYPE *</Typography>
                  <EasySelect
                    propName="phoneType1"
                    connectForm={oldFormConnectForm}
                  />
                </Grid>
                <Grid item xs={12} className={classes.title}>
                  <div
                    id="phoneTextme1"
                    className={
                      requiredFieldErrors.phoneTextme1
                        ? classes.validationChoiceError
                        : ""
                    }
                  >
                    <Typography>USE FOR SMS COMMUNICATION *</Typography>
                    <EasyRadio
                      propName="phoneTextme1"
                      connectForm={oldFormConnectForm}
                    />
                    {requiredFieldErrors.phoneTextme1 ? (
                      <Typography className={classes.validationInlineMessage}>
                        {requiredFieldErrors.phoneTextme1.message}
                      </Typography>
                    ) : null}
                  </div>
                </Grid>
              </Grid>
              {additionalPhones.map(element => {
                return (
                  <Grid
                    key={"additionalPhone" + element}
                    item
                    xs={12}
                    className={classes.title}
                  >
                    <Grid container justify="center" align="flex-end">
                      <Grid item xs={8} className={classes.title}>
                        <EasyPhoneNumber
                          connectForm={props.connectForm}
                          propName={"phoneNumber" + element}
                        />
                      </Grid>
                      <Grid item xs={4} className={classes.title}>
                        <EasyOpenInput
                          connectForm={props.connectForm}
                          propName={"phoneExt" + element}
                          inputProps={{ type: "number" }}
                        />
                      </Grid>
                    </Grid>
                    <Typography style={styles.inputTitle}>
                      {" "}
                      PHONE TYPE *
                    </Typography>
                    <Grid item xs={6} className={classes.title}>
                      <EasySelect
                        propName={"phoneType" + element}
                        connectForm={props.connectForm}
                      />
                    </Grid>
                    <Typography style={styles.inputTitle}>
                      {" "}
                      USE FOR SMS COMMUNICATION *
                    </Typography>
                    <Grid item xs={12} className={classes.title}>
                      <EasyRadio
                        propName={"phoneTextme" + element}
                        connectForm={props.connectForm}
                      />
                    </Grid>
                  </Grid>
                );
              })}
              {props.connectForm.getValue("additionalPhones") < 4 ? (
                <Grid
                  key="additionalPhoneButton"
                  item
                  xs={12}
                  className={classes.addPhoneDiv}
                >
                  <CtaButton
                    onClick={() => {
                      if (
                        props.connectForm.getValue("additionalPhones") === ""
                      ) {
                        props.connectForm.change(1, "additionalPhones");
                        return;
                      }
                      if (props.connectForm.getValue("additionalPhones") < 4) {
                        props.connectForm.change(
                          props.connectForm.getValue("additionalPhones") + 1,
                          "additionalPhones"
                        );
                      }
                    }}
                    color="primary"
                    aria-label="add"
                  >
                    <AddIcon /> Add another Phone Number
                  </CtaButton>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <br />
                <br />
                <br />
                <Typography variant="h5" style={styles.titleStyles}>
                  {" "}
                  Physical Service Location:{" "}
                </Typography>
              </Grid>

              {props.connectForm.getValue("signupclass") === "inside" ? (
                <>
                  <Typography style={styles.typography}>
                    {" "}
                    Service Area:
                  </Typography>
                  <Grid
                    id="areaid"
                    key="areaname"
                    item
                    xs={12}
                    className={
                      requiredFieldErrors.areaid
                        ? classes.validationChoiceError
                        : ""
                    }
                    style={styles.serviceAreaDiv}
                  >
                    <p
                      className={classes.serviceareaname}
                      style={styles.serviceAreaTitle}
                    >
                      {props.areaName.split("-")[0]}
                    </p>
                    <p className={classes.serviceareaname}>
                      {props.areaName.split("-")[1]}
                    </p>
                  </Grid>
                  {requiredFieldErrors.areaid ? (
                    <Typography className={classes.validationInlineMessage}>
                      {requiredFieldErrors.areaid.message}
                    </Typography>
                  ) : null}
                </>
              ) : null}
              <Grid item xs={12} className={classes.title} id="serviceAddress">
                {props.connectForm.getValue("signupclass") === "inside" ? (
                  <Typography style={styles.inputTitle}>
                    Physical Address (no PO Box):
                  </Typography>
                ) : (
                  <Typography style={styles.inputTitle}>
                    Municipal Address:
                  </Typography>
                )}
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={3}
                  className={
                    classes.title +
                    " " +
                    classes.landDesc +
                    " " +
                    classes.noUnderline
                  }
                >
                  <EasyAutocomplete
                    connectForm={oldFormConnectForm}
                    propName="serviceAddress"
                    customOptions={options}
                    onInputChange={handleAutoCompleteInputChange}
                    useLabelAsState
                    useAsSolo
                  />
                </Grid>
              </Grid>

              {props.connectForm.getValue("signupclass") === "inside" ? null : (
                <Grid
                  key="legallanddescription"
                  item
                  xs={12}
                  className={classes.title + " " + classes.godown}
                >
                  <Grid container justify="center">
                    <Grid item xs={12} className={classes.title}>
                      <h4 className={classes.legallandlabel}>
                        And/Or Legal Land Description:
                      </h4>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      className={classes.title + " " + classes.promocode}
                    >
                      <Typography style={styles.inputTitle}>
                        {" "}
                        Quarter{" "}
                      </Typography>
                      <Grid
                        id="ats_quarter"
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        className={classes.title + " " + classes.landDesc}
                      >
                        <EasyAutocomplete
                          propName={"ats_quarter"}
                          connectForm={oldFormConnectForm}
                        />
                      </Grid>
                      <Typography style={styles.inputTitle}>
                        {" "}
                        Section
                      </Typography>
                      <Grid
                        id="ats_section"
                        item
                        xs={12}
                        sm={4}
                        md={2}
                        className={classes.title + " " + classes.landDesc}
                      >
                        <EasyAutocomplete
                          propName={"ats_section"}
                          connectForm={oldFormConnectForm}
                        />
                      </Grid>
                      <Typography style={styles.inputTitle}>
                        {" "}
                        Township{" "}
                      </Typography>
                      <Grid
                        id="ats_township"
                        item
                        xs={12}
                        sm={4}
                        md={2}
                        className={classes.title + " " + classes.landDesc}
                      >
                        <EasyAutocomplete
                          propName={"ats_township"}
                          connectForm={oldFormConnectForm}
                        />
                      </Grid>
                      <Typography style={styles.inputTitle}> Range</Typography>
                      <Grid
                        id="ats_range"
                        item
                        xs={12}
                        sm={6}
                        md={2}
                        className={classes.title + " " + classes.landDesc}
                      >
                        <EasyAutocomplete
                          propName={"ats_range"}
                          connectForm={oldFormConnectForm}
                        />
                      </Grid>
                      <Typography style={styles.inputTitle}>
                        {" "}
                        Meridian
                      </Typography>
                      <Grid
                        id="ats_meridian"
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        className={classes.title + " " + classes.landDesc}
                      >
                        <EasyAutocomplete
                          propName={"ats_meridian"}
                          connectForm={oldFormConnectForm}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <br />
              <br />
              <Typography variant="h5" style={styles.titleStyles}>
                {" "}
                Other Information:
              </Typography>
              <Grid
                key="promocodedescription"
                item
                xs={12}
                md={9}
                className={classes.promoCodeText}
              >
                <p>
                  If you were referred by someone and have a promo code enter it
                  here:
                </p>
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                className={classes.title + " " + classes.promocode}
              >
                <EasyOpenInput
                  connectForm={props.connectForm}
                  propName="promoCode"
                />
              </Grid>
              <Grid item xs={12} className={classes.title}>
                <Typography>Where did you hear about MCSNet? *</Typography>
                <EasySelect
                  propName="advSrc"
                  connectForm={oldFormConnectForm}
                />
              </Grid>
              <Grid item xs={12} md={6} className={classes.title}>
                {props.connectForm.getValue("advSrc") === "Other (specify)" && (
                  <EasyOpenInput
                    connectForm={props.connectForm}
                    propName="advSrcText"
                    multiline="true"
                    rows={4}
                  />
                )}
              </Grid>

              <Grid item xs={12} className={classes.title}>
                <MultilineOpenInput
                  connectForm={props.connectForm}
                  propName="specialNotes"
                />
              </Grid>

              {props.needCaptcha ? (
                <Grid key="recaptcha" item xs={12}>
                  <SignupRecaptcha
                    classes={classes}
                    errorMessage={
                      requiredFieldErrors.recaptcha
                        ? requiredFieldErrors.recaptcha.message
                        : ""
                    }
                    onChange={response => {
                      props.connectForm.change(response, "recaptcha");
                      if (response) {
                        setRecaptchaValidationMessage("");
                      }
                    }}
                    onError={setRecaptchaValidationMessage}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} className={classes.title}>
                <Grid container justify="flex-end">
                  <Grid item xs={12} md={6}>
                    <p className={classes.clearFormText} onClick={clearForm}>
                      Clear Form
                    </p>
                    <CtaButton
                      id={
                        gigair
                          ? "submitGigair"
                          : props.connectForm.getValue("accountType") === "p"
                          ? "submitResidental"
                          : "submitBusiness"
                      }
                      fullWidth
                      disabled={isSubmitting}
                      onClick={submitForm}
                      color="primary"
                    >
                      Submit
                    </CtaButton>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} className={classes.title}>
                <br />
                <br />
                <br />
                <br />
              </Grid>
            </div>
          </SignupCard>
        </div>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(
  loadForm("guest.1/signupform.0", withRouter(Form))
);
